import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useMatchMedia from 'hooks/useMatchMedia';
import useScrollLock from 'hooks/useScrollLock';

import './TablePanelDropdown.styles.scss';

export type TablePanelDropdownRef = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

type TablePanelDropdownProps = {
  className?: string;
  portalClassName?: string;
  trigger: JSX.Element;
  title: string;
  subtitle?: string;
  footer?: ReactNode;
  children: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
};

const TablePanelDropdown: React.ForwardRefRenderFunction<
  TablePanelDropdownRef,
  TablePanelDropdownProps
> = (props, ref) => {
  const {
    className,
    portalClassName,
    trigger,
    title,
    subtitle,
    footer,
    children,
    onOpen,
    onClose,
  } = props;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const isPhablet = useMatchMedia('isPhablet');

  const { lockScroll, unlockScroll } = useScrollLock();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  useImperativeHandle(
    ref,
    () => ({
      isOpen: !!dropdown?.isOpen,
      open: () => dropdown?.setIsOpen(true),
      close: () => dropdown?.setIsOpen(false),
    }),
    [dropdown],
  );

  const classes = useMemo(
    () => classNames('table-panel-dropdown', className),
    [className],
  );

  const handleOnOpen = useCallback(() => {
    if (isPhablet) lockScroll();
    onOpen?.();
  }, [isPhablet, lockScroll, onOpen]);

  const handleOnClose = useCallback(() => {
    if (isPhablet) unlockScroll();
    onClose?.();
  }, [isPhablet, unlockScroll, onClose]);

  return (
    <Dropdown
      ref={dropdownRef}
      className={classes}
      portalClassName={classNames(
        'table-panel-dropdown__portal',
        className,
        portalClassName,
      )}
      renderAsPortal={!isPhablet}
      trigger={trigger}
      onOpen={handleOnOpen}
      onClose={handleOnClose}
    >
      <div className="table-panel-dropdown__panel">
        <div className="table-panel-dropdown__header">
          <p className="title">{title}</p>
          {subtitle && (
            <p className="table-panel-dropdown__subtitle">{subtitle}</p>
          )}
        </div>

        <div className="table-panel-dropdown__body">{children}</div>

        {footer && <div className="table-panel-dropdown__footer">{footer}</div>}
      </div>
    </Dropdown>
  );
};

export default forwardRef(TablePanelDropdown);
